import React from "react";
import { Link } from "react-router-dom";
import { RefreshCcw, SlidersHorizontal } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { analytics } from "../../lib/analytics";
import AdfImage from "./AdfImage";
import ProductCard, { explodeColourCards } from "./ProductCard";
import { listingHeroImage } from "./assets";

function copy(locale, az, en) {
  return locale === "az" ? az : en;
}

export default function ProductListingShell({
  title,
  eyebrow,
  description,
  image,
  imageFit = "cover",
  imagePosition = "center",
  imageHeight = 420,
  heroSlides,
  total = 0,
  loading = false,
  toolbar,
  children,
  heroNote,
  /* Tam naviqasiya izi: [{ label, to }] — sonuncu cari səhifədir. Verilməyəndə
     köhnə iki pilləli «Ana səhifə / eyebrow» qalır. */
  breadcrumbs = null,
  /* Başlığın altındakı alt kateqoriya sətri (CategorySubnav). Verilir-sə
     başlıq mərkəzə keçir — emporium.az kateqoriya səhifəsi kimi. */
  subnav = null,
  align = "start",
  testId = "product-listing",
}) {
  const { locale } = useI18n();
  const heroImage = image || listingHeroImage(0);
  const slides = (Array.isArray(heroSlides) && heroSlides.length ? heroSlides : [heroImage]).filter(Boolean);
  const normalizedFit = imageFit === "contain" ? "contain" : "cover";
  const normalizedHeight = Math.min(520, Math.max(260, Number(imageHeight) || 420));
  const crumbs = (Array.isArray(breadcrumbs) && breadcrumbs.length
    ? breadcrumbs
    : [{ label: eyebrow }]
  ).filter((crumb) => crumb && crumb.label);
  const centered = align === "center" || Boolean(subnav);

  return (
    <main id="main" className="adf-plp-page" data-testid={testId}>
      {slides.length > 0 && (
        <section className={`adf-plp-hero ${slides.length > 1 ? "is-slider" : "is-single"}`} aria-label={copy(locale, "Kolleksiya görüntüsü", "Collection image")}>
          <div className="adf-plp-hero__track" style={{ minHeight: normalizedHeight }}>
            {slides.map((slide, index) => (
              <figure key={index} className="adf-plp-hero__slide">
                <AdfImage
                  source={slide}
                  alt={index === 0 ? title : ""}
                  sizes="100vw"
                  priority={index === 0}
                  fallback={listingHeroImage(2)}
                  className="h-full w-full"
                  style={{ objectFit: normalizedFit, objectPosition: imagePosition || "center" }}
                />
              </figure>
            ))}
          </div>
          {slides.length > 1 && (
            <div className="adf-plp-hero__dots" aria-hidden="true">
              {slides.map((_, index) => <span key={index} className={index === 0 ? "is-active" : ""} />)}
            </div>
          )}
        </section>
      )}

      <section className="adf-container adf-plp-intro" data-align={centered ? "center" : "start"}>
        {/* A landmark, not a div. Assistive technology announces a labelled
            nav and lets a user jump to it; an unlabelled div of links is just
            more text. The separator is decorative and stays hidden. */}
        <nav className="adf-plp-breadcrumb" aria-label={copy(locale, "Naviqasiya izi", "Breadcrumb")} data-testid="plp-breadcrumb">
          <ol>
            <li>
              <Link to="/" className="hover:text-[var(--adf-ink)]">
                {copy(locale, "Ana səhifə", "Home")}
              </Link>
            </li>
            {crumbs.map((crumb, index) => {
              const last = index === crumbs.length - 1;
              return (
                <React.Fragment key={`${crumb.to || ""}:${crumb.label}`}>
                  <li aria-hidden="true">/</li>
                  {last || !crumb.to ? (
                    <li aria-current={last ? "page" : undefined}>{crumb.label}</li>
                  ) : (
                    <li>
                      <Link to={crumb.to} className="hover:text-[var(--adf-ink)]">{crumb.label}</Link>
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
        </nav>
        {centered ? (
          <div className="adf-plp-intro__center">
            <h1 className="adf-plp-title">{title}</h1>
            {subnav}
            {description && <p className="adf-plp-description">{description}</p>}
            {heroNote && <p className="adf-plp-note">{heroNote}</p>}
          </div>
        ) : (
          <div className="adf-plp-intro__body">
            <div>
              <h1 className="adf-plp-title">{title}</h1>
              {description && <p className="adf-plp-description">{description}</p>}
            </div>
            <p className="adf-plp-note">
              {heroNote || copy(
                locale,
                "Ölçü, rəng, material və istifadə məqsədinə görə süzün. Hər məhsul kartından AI Try-On, səbət və qarderob axınına keçmək olur.",
                "Filter by size, colour, fabric and occasion. Product cards connect to AI Try-On, cart and wardrobe flows.",
              )}
            </p>
          </div>
        )}
      </section>

      <section className="adf-container adf-plp-layout" aria-label={copy(locale, "Məhsul siyahısı", "Product listing")}>
        {toolbar}
        <div className="adf-plp-results">
          {children}
        </div>
      </section>
    </main>
  );
}

export function ProductListingResults({
  products,
  loading,
  loadError,
  onRetry,
  onClear,
  emptyTitle,
  emptyDescription,
  emptyActions,
  productTestId = "listing-product",
  listName = "catalogue",
}) {
  const { locale } = useI18n();

  // One impression event per rendered page of results. Keyed on the handles so
  // paginating or filtering re-reports, while a re-render that changes nothing
  // does not.
  const shownKey = (products || []).map((p) => p.handle).join(",");
  React.useEffect(() => {
    if (!loading && products?.length) analytics.viewItemList(listName, products, { category: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey, loading, listName]);

  if (loading) {
    return (
      <div className="adf-product-grid" data-testid="plp-loading" aria-label={copy(locale, "Məhsullar yüklənir", "Loading products")}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-[3/4] animate-pulse bg-[var(--adf-gray-6)]" />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <section className="adf-plp-state" role="alert" data-testid="plp-error">
        <RefreshCcw className="mx-auto mb-4 h-6 w-6 text-[var(--adf-brass)]" aria-hidden="true" />
        <h2>{copy(locale, "Məhsullar yüklənmədi", "The edit could not be loaded")}</h2>
        <p>{copy(locale, "Bağlantını yoxlayıb yenidən sınayın.", "Check your connection and try again.")}</p>
        <button type="button" className="adf-cta adf-cta-outline mt-6" onClick={onRetry}>
          {copy(locale, "Yenidən sına", "Try again")}
        </button>
      </section>
    );
  }

  if (!products.length) {
    return (
      <section className="adf-plp-state" data-testid="plp-empty">
        <SlidersHorizontal className="mx-auto mb-4 h-6 w-6 text-[var(--adf-brass)]" aria-hidden="true" />
        <h2>{emptyTitle || copy(locale, "Uyğun məhsul tapılmadı", "No pieces match these filters")}</h2>
        <p>{emptyDescription || copy(locale, "Filterləri azaldın və kolleksiyaya yenidən baxın.", "Remove a filter to broaden the edit.")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          {onClear && (
            <button type="button" className="adf-cta adf-cta-outline" onClick={onClear}>
              {copy(locale, "Filterləri təmizlə", "Clear filters")}
            </button>
          )}
          {emptyActions}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="plp-grid-heading">
      {/* The page h1 was followed straight by the cards' h3, so the outline
          skipped a level on every listing. The grid is a section of the page
          and gets the h2 that says so; it is not shown because the h1 above it
          already names what is being listed. */}
      <h2 id="plp-grid-heading" className="sr-only">
        {copy(locale, "Məhsullar", "Products")}
      </h2>
      <div className="adf-product-grid" data-testid="plp-product-grid" aria-busy="false">
        {explodeColourCards(products).map((product, index) => (
          <ProductCard key={product.__cardKey || `${product.id}-${index}`} product={product} index={index} testId={productTestId} listName={listName} />
        ))}
      </div>
    </section>
  );
}
